import React, { useState } from 'react';
import { X, Building, User, Phone, Mail, MapPin, Calendar, Send } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { emplacementsData } from '../data/emplacements';

const QuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    // Section 1: Informations société
    companyName: '',
    sector: '',
    address: '',
    
    // Section 2: Contact
    contactName: '',
    phone: '',
    email: '',
    
    // Section 3: Choix des panneaux
    deroulantsSelected: false,
    deroulantsCount: 1,
    deroulantsEmplacements: {},
    
    ledSelected: false,
    ledCount: 1,
    ledEmplacements: {},
    
    grandFormatSelected: false,
    grandFormatCount: 1,
    grandFormatEmplacements: {},
    
    panneauxPoteauxSelected: false,
    panneauxPoteauxCount: 1,
    panneauxPoteauxEmplacements: {},
    
    // Section 4: Période
    dateDebut: '',
    dateFin: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEmplacementToggle = (type, emplacementId) => {
    setFormData(prev => ({
      ...prev,
      [`${type}Emplacements`]: {
        ...prev[`${type}Emplacements`],
        [emplacementId]: prev[`${type}Emplacements`][emplacementId] 
          ? undefined 
          : { selected: true, nombreAffiches: 1 }
      }
    }));
  };

  const handleAffichesChange = (type, emplacementId, value) => {
    setFormData(prev => ({
      ...prev,
      [`${type}Emplacements`]: {
        ...prev[`${type}Emplacements`],
        [emplacementId]: {
          ...prev[`${type}Emplacements`][emplacementId],
          nombreAffiches: parseInt(value) || 1
        }
      }
    }));
  };

  const generateWhatsAppMessage = () => {
    let message = `🆕 *Nouvelle demande de devis SESAM*\n\n`;
    
    // Informations société
    message += `📋 *Informations société*\n`;
    message += `• Nom : ${formData.companyName}\n`;
    message += `• Secteur : ${formData.sector}\n`;
    message += `• Adresse : ${formData.address}\n\n`;
    
    // Contact
    message += `👤 *Contact*\n`;
    message += `• Nom : ${formData.contactName}\n`;
    message += `• Téléphone : ${formData.phone}\n`;
    message += `• Email : ${formData.email}\n\n`;
    
    // Détails des panneaux
    message += `📍 *Détails des panneaux*\n`;
    
    if (formData.deroulantsSelected) {
      message += `\n☑️ *Panneaux déroulants*\n`;
      message += `• Nombre de panneaux : ${formData.deroulantsCount}\n`;
      message += `• Emplacements et affiches :\n`;
      Object.keys(formData.deroulantsEmplacements).forEach(id => {
        if (formData.deroulantsEmplacements[id]?.selected) {
          const emplacement = emplacementsData.deroulants.find(e => e.id === parseInt(id));
          const nbAffiches = formData.deroulantsEmplacements[id].nombreAffiches;
          message += `  ${id}. ${emplacement.name} : ${nbAffiches} affiche(s)\n`;
        }
      });
    }
    
    if (formData.ledSelected) {
      message += `\n☑️ *Panneaux LED*\n`;
      message += `• Nombre de panneaux : ${formData.ledCount}\n`;
      message += `• Emplacements et affiches :\n`;
      Object.keys(formData.ledEmplacements).forEach(id => {
        if (formData.ledEmplacements[id]?.selected) {
          const emplacement = emplacementsData.led.find(e => e.id === parseInt(id));
          const nbAffiches = formData.ledEmplacements[id].nombreAffiches;
          message += `  ${id}. ${emplacement.name} : ${nbAffiches} affiche(s)\n`;
        }
      });
    }
    
    if (formData.grandFormatSelected) {
      message += `\n☑️ *Panneau grand format*\n`;
      message += `• Nombre de panneaux : ${formData.grandFormatCount}\n`;
      message += `• Emplacement et affiches :\n`;
      Object.keys(formData.grandFormatEmplacements).forEach(id => {
        if (formData.grandFormatEmplacements[id]?.selected) {
          const emplacement = emplacementsData.grandFormat.find(e => e.id === parseInt(id));
          const nbAffiches = formData.grandFormatEmplacements[id].nombreAffiches;
          message += `  ${id}. ${emplacement.name} : ${nbAffiches} affiche(s)\n`;
        }
      });
    }
    
    if (formData.panneauxPoteauxSelected) {
      message += `\n☑️ *Panneaux poteaux*\n`;
      message += `• Nombre de panneaux : ${formData.panneauxPoteauxCount}\n`;
      message += `• Emplacements et affiches :\n`;
      Object.keys(formData.panneauxPoteauxEmplacements).forEach(id => {
        if (formData.panneauxPoteauxEmplacements[id]?.selected) {
          const emplacement = emplacementsData.panneauxPoteaux.find(e => e.id === parseInt(id));
          const nbAffiches = formData.panneauxPoteauxEmplacements[id].nombreAffiches;
          message += `  ${id}. ${emplacement.name} : ${nbAffiches} affiche(s)\n`;
        }
      });
    }
    
    // Période
    message += `\n📅 *Détails de la période de diffusion*\n`;
    message += `• Du : ${formData.dateDebut || 'Non spécifié'} au ${formData.dateFin || 'Non spécifié'}\n`;
    
    return encodeURIComponent(message);
  };

  const handleSubmit = async () => {
    // Validation basique
    if (!formData.companyName || !formData.contactName || !formData.phone) {
      alert('Veuillez remplir les champs obligatoires');
      return;
    }
    
    if (!formData.deroulantsSelected && !formData.ledSelected && !formData.grandFormatSelected && !formData.panneauxPoteauxSelected) {
      alert('Veuillez sélectionner au moins un type de panneau');
      return;
    }
    
    // Préparer le message détaillé pour le CRM
    let detailsMessage = '';
    
    // Détails des panneaux pour le CRM
    if (formData.deroulantsSelected) {
      detailsMessage += '📍 Panneaux déroulants:\n';
      detailsMessage += `Nombre: ${formData.deroulantsCount}\n`;
      Object.keys(formData.deroulantsEmplacements).forEach(id => {
        if (formData.deroulantsEmplacements[id]?.selected) {
          const emplacement = emplacementsData.deroulants.find(e => e.id === parseInt(id));
          const nbAffiches = formData.deroulantsEmplacements[id].nombreAffiches;
          detailsMessage += `- ${emplacement.name}: ${nbAffiches} affiche(s)\n`;
        }
      });
      detailsMessage += '\n';
    }
    
    if (formData.ledSelected) {
      detailsMessage += '📍 Panneaux LED:\n';
      detailsMessage += `Nombre: ${formData.ledCount}\n`;
      Object.keys(formData.ledEmplacements).forEach(id => {
        if (formData.ledEmplacements[id]?.selected) {
          const emplacement = emplacementsData.led.find(e => e.id === parseInt(id));
          const nbAffiches = formData.ledEmplacements[id].nombreAffiches;
          detailsMessage += `- ${emplacement.name}: ${nbAffiches} affiche(s)\n`;
        }
      });
      detailsMessage += '\n';
    }
    
    if (formData.grandFormatSelected) {
      detailsMessage += '📍 Panneau grand format:\n';
      detailsMessage += `Nombre: ${formData.grandFormatCount}\n`;
      Object.keys(formData.grandFormatEmplacements).forEach(id => {
        if (formData.grandFormatEmplacements[id]?.selected) {
          const emplacement = emplacementsData.grandFormat.find(e => e.id === parseInt(id));
          const nbAffiches = formData.grandFormatEmplacements[id].nombreAffiches;
          detailsMessage += `- ${emplacement.name}: ${nbAffiches} affiche(s)\n`;
        }
      });
      detailsMessage += '\n';
    }
    
    if (formData.panneauxPoteauxSelected) {
      detailsMessage += '📍 Panneaux poteaux:\n';
      detailsMessage += `Nombre: ${formData.panneauxPoteauxCount}\n`;
      Object.keys(formData.panneauxPoteauxEmplacements).forEach(id => {
        if (formData.panneauxPoteauxEmplacements[id]?.selected) {
          const emplacement = emplacementsData.panneauxPoteaux.find(e => e.id === parseInt(id));
          const nbAffiches = formData.panneauxPoteauxEmplacements[id].nombreAffiches;
          detailsMessage += `- ${emplacement.name}: ${nbAffiches} affiche(s)\n`;
        }
      });
      detailsMessage += '\n';
    }
    
    detailsMessage += `📅 Période: Du ${formData.dateDebut || 'Non spécifié'} au ${formData.dateFin || 'Non spécifié'}`;
    
    // Préparer les données pour le CRM
    const crmPayload = {
      societe: formData.companyName,
      secteur: formData.sector,
      adresse: formData.address,
      nom_complet: formData.contactName,
      telephone: formData.phone,
      email: formData.email,
      message: detailsMessage,
      source: "sesamrca.com"
    };
    
    console.log('📤 Envoi au CRM...', crmPayload);
    
    // Envoi au CRM avec gestion d'erreur améliorée
    try {
      const response = await fetch("https://www.crmsesam.com/api/public/devis-request", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(crmPayload),
        mode: 'cors'
      });
      
      console.log('✅ Réponse CRM:', response.status, response.statusText);
      
      if (response.ok) {
        console.log('✅ Demande envoyée au CRM avec succès !');
      } else {
        console.warn('⚠️ CRM a répondu mais avec erreur:', response.status);
        const errorText = await response.text();
        console.warn('Détails:', errorText);
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi au CRM:', error);
      console.error('Type d\'erreur:', error.name);
      console.error('Message:', error.message);
      
      // Si CORS, essayer sans www
      if (error.name === 'TypeError' || error.message.includes('CORS')) {
        console.log('🔄 Tentative sans www...');
        try {
          const response2 = await fetch("https://crmsesam.com/api/public/devis-request", {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Accept": "application/json"
            },
            body: JSON.stringify(crmPayload),
            mode: 'cors'
          });
          console.log('✅ Réponse CRM (sans www):', response2.status);
        } catch (error2) {
          console.error('❌ Échec aussi sans www:', error2.message);
        }
      }
    }
    
    // Envoi WhatsApp (toujours faire même si CRM échoue)
    const message = generateWhatsAppMessage();
    const whatsappUrl = `https://wa.me/23675503702?text=${message}`;
    window.open(whatsappUrl, '_blank');
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-8 py-6 z-10">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-full"
          >
            <X size={24} />
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Demander un devis</h2>
          <p className="text-gray-600 mt-1">Remplissez le formulaire ci-dessous et nous vous enverrons votre devis personnalisé.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 p-8">
          {/* Colonne gauche : Informations + Contact */}
          <div className="space-y-8">
            {/* Section 1 : Informations société */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                1. Informations sur votre société
              </h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="companyName">Nom de la société *</Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Ex : Mon Entreprise SARL"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="sector">Secteur d'activité</Label>
                  <Input
                    id="sector"
                    name="sector"
                    value={formData.sector}
                    onChange={handleInputChange}
                    placeholder="Ex : Immobilier, Boissons, Automobile..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Ex : Yaoundé, Cameroun"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Section 2 : Contact */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                2. Contact
              </h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="contactName">Nom complet *</Label>
                  <Input
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    placeholder="Ex : Jean Dupont"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Ex : +236 75 50 37 02"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Ex : contact@monentreprise.com"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Colonne centrale : Choix des panneaux */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              3. Choix des panneaux
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Sélectionnez le(s) type(s) de panneaux souhaité(s)
            </p>

            <div className="space-y-6">
              {/* Panneaux Déroulants */}
              <div className="border-2 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="deroulantsSelected"
                    name="deroulantsSelected"
                    checked={formData.deroulantsSelected}
                    onChange={handleInputChange}
                    className="mt-1 h-5 w-5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="deroulantsSelected" className="text-base font-bold cursor-pointer">
                      Panneaux déroulants
                    </Label>
                    <p className="text-sm text-gray-600">10 emplacements par panneau</p>
                  </div>
                </div>

                {formData.deroulantsSelected && (
                  <div className="space-y-3 mt-4 pl-8">
                    <div>
                      <Label>Nombre de panneaux</Label>
                      <Input
                        type="number"
                        name="deroulantsCount"
                        min="1"
                        value={formData.deroulantsCount}
                        onChange={handleInputChange}
                        className="w-24 mt-1"
                      />
                    </div>
                    
                    <div className="mt-4">
                      <Label className="text-sm font-semibold mb-2 block">
                        Sélectionnez les emplacements (max {formData.deroulantsCount})
                      </Label>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {emplacementsData.deroulants.map((emp) => (
                          <div key={emp.id} className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={formData.deroulantsEmplacements[emp.id]?.selected || false}
                              onChange={() => handleEmplacementToggle('deroulants', emp.id)}
                              className="h-4 w-4"
                            />
                            <span className="text-sm flex-1">{emp.id}. {emp.name}</span>
                            {formData.deroulantsEmplacements[emp.id]?.selected && (
                              <select
                                value={formData.deroulantsEmplacements[emp.id]?.nombreAffiches || 1}
                                onChange={(e) => handleAffichesChange('deroulants', emp.id, e.target.value)}
                                className="w-16 px-2 py-1 border rounded text-sm"
                              >
                                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                  <option key={n} value={n}>{n}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Panneaux LED */}
              <div className="border-2 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="ledSelected"
                    name="ledSelected"
                    checked={formData.ledSelected}
                    onChange={handleInputChange}
                    className="mt-1 h-5 w-5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="ledSelected" className="text-base font-bold cursor-pointer">
                      Panneaux LED
                    </Label>
                    <p className="text-sm text-gray-600">3 emplacements par panneau</p>
                  </div>
                </div>

                {formData.ledSelected && (
                  <div className="space-y-3 mt-4 pl-8">
                    <div>
                      <Label>Nombre de panneaux</Label>
                      <Input
                        type="number"
                        name="ledCount"
                        min="1"
                        value={formData.ledCount}
                        onChange={handleInputChange}
                        className="w-24 mt-1"
                      />
                    </div>
                    
                    <div className="mt-4">
                      <Label className="text-sm font-semibold mb-2 block">
                        Sélectionnez les emplacements (max {formData.ledCount})
                      </Label>
                      <div className="space-y-2">
                        {emplacementsData.led.map((emp) => (
                          <div key={emp.id} className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={formData.ledEmplacements[emp.id]?.selected || false}
                              onChange={() => handleEmplacementToggle('led', emp.id)}
                              className="h-4 w-4"
                            />
                            <span className="text-sm flex-1">{emp.id}. {emp.name}</span>
                            {formData.ledEmplacements[emp.id]?.selected && (
                              <select
                                value={formData.ledEmplacements[emp.id]?.nombreAffiches || 1}
                                onChange={(e) => handleAffichesChange('led', emp.id, e.target.value)}
                                className="w-16 px-2 py-1 border rounded text-sm"
                              >
                                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                  <option key={n} value={n}>{n}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Panneau Grand Format */}
              <div className="border-2 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="grandFormatSelected"
                    name="grandFormatSelected"
                    checked={formData.grandFormatSelected}
                    onChange={handleInputChange}
                    className="mt-1 h-5 w-5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="grandFormatSelected" className="text-base font-bold cursor-pointer">
                      Panneau grand format
                    </Label>
                    <p className="text-sm text-gray-600">1 emplacement par panneau</p>
                  </div>
                </div>

                {formData.grandFormatSelected && (
                  <div className="space-y-3 mt-4 pl-8">
                    <div>
                      <Label>Nombre de panneaux</Label>
                      <Input
                        type="number"
                        name="grandFormatCount"
                        min="1"
                        value={formData.grandFormatCount}
                        onChange={handleInputChange}
                        className="w-24 mt-1"
                      />
                    </div>
                    
                    <div className="mt-4">
                      <Label className="text-sm font-semibold mb-2 block">
                        Emplacement
                      </Label>
                      <div className="space-y-2">
                        {emplacementsData.grandFormat.map((emp) => (
                          <div key={emp.id} className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={formData.grandFormatEmplacements[emp.id]?.selected || false}
                              onChange={() => handleEmplacementToggle('grandFormat', emp.id)}
                              className="h-4 w-4"
                            />
                            <span className="text-sm flex-1">{emp.id}. {emp.name}</span>
                            {formData.grandFormatEmplacements[emp.id]?.selected && (
                              <select
                                value={formData.grandFormatEmplacements[emp.id]?.nombreAffiches || 1}
                                onChange={(e) => handleAffichesChange('grandFormat', emp.id, e.target.value)}
                                className="w-16 px-2 py-1 border rounded text-sm"
                              >
                                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                  <option key={n} value={n}>{n}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Panneaux Poteaux */}
              <div className="border-2 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="panneauxPoteauxSelected"
                    name="panneauxPoteauxSelected"
                    checked={formData.panneauxPoteauxSelected}
                    onChange={handleInputChange}
                    className="mt-1 h-5 w-5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="panneauxPoteauxSelected" className="text-base font-bold cursor-pointer">
                      Panneaux poteaux
                    </Label>
                    <p className="text-sm text-gray-600">4 emplacements par panneau</p>
                  </div>
                </div>

                {formData.panneauxPoteauxSelected && (
                  <div className="space-y-3 mt-4 pl-8">
                    <div>
                      <Label>Nombre de panneaux</Label>
                      <Input
                        type="number"
                        name="panneauxPoteauxCount"
                        min="1"
                        value={formData.panneauxPoteauxCount}
                        onChange={handleInputChange}
                        className="w-24 mt-1"
                      />
                    </div>
                    
                    <div className="mt-4">
                      <Label className="text-sm font-semibold mb-2 block">
                        Sélectionnez les emplacements (max {formData.panneauxPoteauxCount})
                      </Label>
                      <div className="space-y-2">
                        {emplacementsData.panneauxPoteaux.map((emp) => (
                          <div key={emp.id} className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={formData.panneauxPoteauxEmplacements[emp.id]?.selected || false}
                              onChange={() => handleEmplacementToggle('panneauxPoteaux', emp.id)}
                              className="h-4 w-4"
                            />
                            <span className="text-sm flex-1">{emp.id}. {emp.name}</span>
                            {formData.panneauxPoteauxEmplacements[emp.id]?.selected && (
                              <select
                                value={formData.panneauxPoteauxEmplacements[emp.id]?.nombreAffiches || 1}
                                onChange={(e) => handleAffichesChange('panneauxPoteaux', emp.id, e.target.value)}
                                className="w-16 px-2 py-1 border rounded text-sm"
                              >
                                {[...Array(25)].map((_, i) => (
                                  <option key={i+1} value={i+1}>{i+1}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 4 : Période */}
              <div className="border-t pt-6">
                <h4 className="text-base font-bold text-gray-900 mb-4">
                  4. Détails de la période de diffusion
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dateDebut">Date de début</Label>
                    <Input
                      id="dateDebut"
                      name="dateDebut"
                      type="date"
                      value={formData.dateDebut}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dateFin">Date de fin</Label>
                    <Input
                      id="dateFin"
                      name="dateFin"
                      type="date"
                      value={formData.dateFin}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne droite : Résumé */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              5. Résumé de votre demande
            </h3>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-200">
              <div className="bg-white rounded-xl p-6 mb-6 text-center">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-blue-100 rounded-full p-4">
                    <Send className="w-10 h-10 text-blue-600" />
                  </div>
                </div>
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  Votre demande sera envoyée à un de nos conseillers
                </p>
                <p className="text-sm text-gray-600">
                  Notre équipe vous contactera dans les plus brefs délais
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-900 text-center">
                  <strong>ℹ️ En cliquant sur "Demander"</strong>, votre demande sera envoyée directement à notre équipe
                </p>
              </div>

              <Button
                onClick={handleSubmit}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-14 text-lg font-bold rounded-xl"
              >
                <Send className="w-5 h-5 mr-2" />
                Demander
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
